/**
 * Book-level protected terms glossary for translation (do-not-translate / transliterate).
 * Source of truth: `book.metadata.protectedTerms`. Seeded from character/location rosters.
 */

import {
  getCharactersFromBookMetadata,
  type BookCharacter,
} from "./characterRoster";
import {
  getLocationsFromBookMetadata,
  type BookLocation,
} from "./locationRoster";
import { tipTapJsonToPlainText, tryParseStringifiedTipTapDoc } from "./tipTapPlainText";

export type ProtectedTermKind = "character" | "location" | "properNoun" | "other";
export type ProtectedTermPolicy = "keep" | "transliterate";

export type ProtectedTerm = {
  id: string;
  surface: string;
  aliases: string[];
  kind: ProtectedTermKind;
  sourceId?: string;
  policy: ProtectedTermPolicy;
  note?: string;
};

export function generateProtectedTermId(): string {
  return `pt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSurface(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function surfaceKey(value: string): string {
  return normalizeSurface(value).toLowerCase();
}

export function normalizeProtectedTerm(raw: unknown, nowIso = new Date().toISOString()): ProtectedTerm | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const surface = typeof rec.surface === "string" ? normalizeSurface(rec.surface) : "";
  if (!surface) return null;
  const kind =
    rec.kind === "character" ||
    rec.kind === "location" ||
    rec.kind === "properNoun" ||
    rec.kind === "other"
      ? rec.kind
      : "other";
  const policy: ProtectedTermPolicy = rec.policy === "transliterate" ? "transliterate" : "keep";
  const aliases = Array.isArray(rec.aliases)
    ? rec.aliases
        .filter((a): a is string => typeof a === "string")
        .map(normalizeSurface)
        .filter(Boolean)
    : [];
  const id =
    typeof rec.id === "string" && rec.id.trim()
      ? rec.id.trim()
      : generateProtectedTermId();
  const sourceId = typeof rec.sourceId === "string" && rec.sourceId.trim() ? rec.sourceId.trim() : undefined;
  const note = typeof rec.note === "string" && rec.note.trim() ? rec.note.trim() : undefined;
  void nowIso;
  return { id, surface, aliases, kind, policy, ...(sourceId ? { sourceId } : {}), ...(note ? { note } : {}) };
}

export function normalizeProtectedTerms(raw: unknown): ProtectedTerm[] {
  if (!Array.isArray(raw)) return [];
  const out: ProtectedTerm[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const term = normalizeProtectedTerm(item);
    if (!term) continue;
    const key = surfaceKey(term.surface);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(term);
  }
  return out;
}

export function getProtectedTermsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): ProtectedTerm[] {
  if (!metadata) return [];
  return normalizeProtectedTerms(metadata.protectedTerms);
}

export function withProtectedTermsInMetadata(
  metadata: Record<string, unknown> | undefined | null,
  terms: ProtectedTerm[]
): Record<string, unknown> {
  return {
    ...(metadata && typeof metadata === "object" ? metadata : {}),
    protectedTerms: normalizeProtectedTerms(terms),
  };
}

function termFromCharacter(character: BookCharacter): ProtectedTerm {
  return {
    id: generateProtectedTermId(),
    surface: character.name,
    aliases: [...character.aliases],
    kind: "character",
    sourceId: character.id,
    policy: "keep",
  };
}

function termFromLocation(location: BookLocation): ProtectedTerm {
  return {
    id: generateProtectedTermId(),
    surface: location.name,
    aliases: [...location.aliases],
    kind: "location",
    sourceId: location.id,
    policy: "keep",
  };
}

/**
 * Seed glossary from character + location rosters.
 * Preserves author-only terms (properNoun / other / no sourceId) and author policy edits
 * for roster-linked terms when surfaces still match.
 */
export function seedProtectedTermsFromRosters(params: {
  metadata: Record<string, unknown> | undefined | null;
  existing?: ProtectedTerm[];
}): ProtectedTerm[] {
  const existing = normalizeProtectedTerms(params.existing ?? getProtectedTermsFromMetadata(params.metadata));
  const characters = getCharactersFromBookMetadata(params.metadata);
  const locations = getLocationsFromBookMetadata(params.metadata);

  const bySource = new Map<string, ProtectedTerm>();
  const bySurface = new Map<string, ProtectedTerm>();
  for (const term of existing) {
    bySurface.set(surfaceKey(term.surface), term);
    if (term.sourceId) bySource.set(`${term.kind}:${term.sourceId}`, term);
  }

  const next: ProtectedTerm[] = [];
  const usedKeys = new Set<string>();

  for (const character of characters) {
    const seeded = termFromCharacter(character);
    const prev =
      bySource.get(`character:${character.id}`) ?? bySurface.get(surfaceKey(character.name));
    const merged: ProtectedTerm = prev
      ? {
          ...seeded,
          id: prev.id,
          policy: prev.policy,
          note: prev.note,
          aliases: Array.from(new Set([...seeded.aliases, ...prev.aliases].map(normalizeSurface).filter(Boolean))),
        }
      : seeded;
    next.push(merged);
    usedKeys.add(surfaceKey(merged.surface));
  }

  for (const location of locations) {
    const seeded = termFromLocation(location);
    const prev =
      bySource.get(`location:${location.id}`) ?? bySurface.get(surfaceKey(location.name));
    const merged: ProtectedTerm = prev
      ? {
          ...seeded,
          id: prev.id,
          policy: prev.policy,
          note: prev.note,
          aliases: Array.from(new Set([...seeded.aliases, ...prev.aliases].map(normalizeSurface).filter(Boolean))),
        }
      : seeded;
    next.push(merged);
    usedKeys.add(surfaceKey(merged.surface));
  }

  for (const term of existing) {
    if (usedKeys.has(surfaceKey(term.surface))) continue;
    // Keep author-only / proper-noun terms and orphaned roster terms the author kept.
    if (term.kind === "properNoun" || term.kind === "other" || !term.sourceId) {
      next.push(term);
      usedKeys.add(surfaceKey(term.surface));
    }
  }

  return next.sort((a, b) => a.surface.localeCompare(b.surface, undefined, { sensitivity: "base" }));
}

/** Walk TipTap JSON (or string) and collect properNoun mark surfaces. */
export function collectProperNounSurfacesFromContent(content: unknown): Array<{ surface: string; termId?: string }> {
  const doc =
    typeof content === "string"
      ? tryParseStringifiedTipTapDoc(content) ?? null
      : content && typeof content === "object"
        ? (content as Record<string, unknown>)
        : null;
  if (!doc) return [];

  const out: Array<{ surface: string; termId?: string }> = [];

  function walk(node: unknown): void {
    if (!node || typeof node !== "object") return;
    const rec = node as Record<string, unknown>;
    if (rec.type === "text" && typeof rec.text === "string" && Array.isArray(rec.marks)) {
      const proper = rec.marks.find(
        (m) => m && typeof m === "object" && (m as { type?: string }).type === "properNoun"
      ) as { type: string; attrs?: { termId?: string | null } } | undefined;
      if (proper) {
        const surface = normalizeSurface(rec.text);
        if (surface) {
          const termId =
            typeof proper.attrs?.termId === "string" && proper.attrs.termId.trim()
              ? proper.attrs.termId.trim()
              : undefined;
          out.push({ surface, ...(termId ? { termId } : {}) });
        }
      }
    }
    const children = Array.isArray(rec.content) ? rec.content : [];
    for (const child of children) walk(child);
  }

  walk(doc);
  return out;
}

/** Upsert properNoun marks found in chapter contents into the glossary. */
export function upsertProperNounMarksIntoTerms(
  terms: ProtectedTerm[],
  chapterContents: unknown[]
): ProtectedTerm[] {
  const next = normalizeProtectedTerms(terms);
  const bySurface = new Map(next.map((t) => [surfaceKey(t.surface), t]));

  for (const content of chapterContents) {
    for (const hit of collectProperNounSurfacesFromContent(content)) {
      const key = surfaceKey(hit.surface);
      const existing = bySurface.get(key);
      if (existing) {
        if (hit.termId && !existing.sourceId) {
          existing.id = hit.termId;
        }
        continue;
      }
      const term: ProtectedTerm = {
        id: hit.termId || generateProtectedTermId(),
        surface: hit.surface,
        aliases: [],
        kind: "properNoun",
        policy: "keep",
      };
      next.push(term);
      bySurface.set(key, term);
    }
  }
  return next;
}

export function formatProtectedTermsForPrompt(terms: ProtectedTerm[]): string {
  const keep = terms.filter((t) => t.policy === "keep");
  const transliterate = terms.filter((t) => t.policy === "transliterate");
  const lines: string[] = [];

  if (keep.length) {
    lines.push("Do NOT translate or alter these exact strings (keep spelling and casing):");
    for (const term of keep) {
      const aliases = term.aliases.length ? ` (also: ${term.aliases.join(", ")})` : "";
      const note = term.note ? ` — ${term.note}` : "";
      lines.push(`- ${term.surface}${aliases}${note}`);
    }
  }
  if (transliterate.length) {
    lines.push(
      "These names may be transliterated into the target language's script/phonetics if needed, but do not invent new names:"
    );
    for (const term of transliterate) {
      const aliases = term.aliases.length ? ` (also: ${term.aliases.join(", ")})` : "";
      lines.push(`- ${term.surface}${aliases}`);
    }
  }
  return lines.join("\n");
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  const lowerHay = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let count = 0;
  let idx = 0;
  while (idx < lowerHay.length) {
    const found = lowerHay.indexOf(lowerNeedle, idx);
    if (found < 0) break;
    count += 1;
    idx = found + Math.max(1, lowerNeedle.length);
  }
  return count;
}

export type MissingKeepTerm = {
  surface: string;
  expectedMin: number;
  found: number;
};

/**
 * Verify each keep-policy term that appears in the source still appears in the translation
 * at least as often (or ≥1 if present in source).
 */
export function findMissingKeepTerms(params: {
  sourcePlain: string;
  translatedPlain: string;
  terms: ProtectedTerm[];
}): MissingKeepTerm[] {
  const missing: MissingKeepTerm[] = [];
  for (const term of params.terms) {
    if (term.policy !== "keep") continue;
    const surfaces = [term.surface, ...term.aliases].map(normalizeSurface).filter(Boolean);
    let sourceCount = 0;
    let translatedCount = 0;
    for (const surface of surfaces) {
      sourceCount += countOccurrences(params.sourcePlain, surface);
      translatedCount += countOccurrences(params.translatedPlain, surface);
    }
    if (sourceCount <= 0) continue;
    // Require at least one occurrence when the term appears in the source chapter.
    if (translatedCount < 1) {
      missing.push({
        surface: term.surface,
        expectedMin: 1,
        found: translatedCount,
      });
    }
  }
  return missing;
}

export function chapterContentToPlainForTerms(content: unknown): string {
  if (typeof content === "string") {
    const doc = tryParseStringifiedTipTapDoc(content);
    if (doc) return tipTapJsonToPlainText(doc);
    return content;
  }
  if (content && typeof content === "object") {
    return tipTapJsonToPlainText(content);
  }
  return "";
}
